import { useRecoverExpedition } from "@/api/expedition.api";
import { useCreateNoteBo } from "@/api/notes.api";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { Textarea } from "@/components/ui/textarea";
import { getExpeditionIdentifier } from "@/lib/expedition.util";
import {
  GetAllExpeditionBoResponseDtoExpedition,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { Loader2 } from "lucide-react";
import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  open: boolean;
  onClose: () => void;
  expedition: GetAllExpeditionBoResponseDtoExpedition | GetOneExpeditionBoResponseDto;
};

const FORM_ID = "recover-expedition-form";

export const RecoverExpeditionModal = ({ open, onClose, expedition }: Props) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const [note, setNote] = useState<string>("");
  const { mutateAsync: recoverExpedition, isPending: isRecoverPending } = useRecoverExpedition();
  const { mutateAsync: createNote, isPending: isCreateNotePending } = useCreateNoteBo();

  const identifier = getExpeditionIdentifier(expedition) ?? "";
  const trimmedNote = note.trim();
  const isPending = isCreateNotePending || isRecoverPending;
  const canSubmit = !!trimmedNote && !isPending;

  const handleRecover = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    await createNote({ id: expedition.id, payload: trimmedNote });
    await recoverExpedition(expedition.id);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={!isPending}
      title={t("recoverModal.title", { identifier })}
      description={t("recoverModal.description", { identifier })}
      footer={
        <div className="flex w-full flex-row justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={isPending}
          >
            {t("common:actions.cancel")}
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            variant="default"
            className="flex-1"
            disabled={!canSubmit}
          >
            {isPending ? <Loader2 className="animate-spin" /> : t("common:actions.confirm")}
          </Button>
        </div>
      }
    >
      <form id={FORM_ID} onSubmit={handleRecover}>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("recoverModal.notePlaceholder")}
          disabled={isPending}
        />
      </form>
    </Modal>
  );
};
